import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useT } from "../i18n";

/** Decodifica el payload del JWT sin verificar (solo para extraer el email del
 *  user destinatario del reset y darle un saludo personalizado). El backend
 *  sigue validando la firma del token al hacer el POST. */
function nameFromToken(token: string): string | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const email = (payload?.sub as string) || "";
    const local = email.split("@")[0];
    if (!local) return null;
    return local
      .split(/[._\-+]/)
      .filter(Boolean)
      .map(p => p.charAt(0).toUpperCase() + p.slice(1))
      .join(" ");
  } catch {
    return null;
  }
}

export default function ResetPasswordPage() {
  const t = useT();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";
  const name = useMemo(() => nameFromToken(token), [token]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError(t("auth.pwdTooShort")); return; }
    if (password !== confirm) { setError(t("auth.pwdMismatch")); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail ?? t("auth.linkInvalidExpired"));
      }
      setDone(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("auth.unknownError"));
    } finally {
      setLoading(false);
    }
  };

  // Paleta HelixScan
  const TEAL = "#0891b2";
  const TEAL_DARK = "#0e7490";
  const TEXT_DARK = "#0c4a5e";
  const TEXT_MUTED = "rgba(12,74,94,.55)";
  const BG_GRADIENT = "linear-gradient(145deg,#d4edf2,#e3f2f4,#cee8ec)";

  if (!token) {
    return (
      <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:BG_GRADIENT }}>
        <div style={{ textAlign:"center",padding:"2rem" }}>
          <p style={{ color:"#dc2626",marginBottom:"1rem" }}>{t("auth.linkInvalid")}</p>
          <Link to="/login" style={{ color:TEAL,fontWeight:600 }}>{t("auth.backToLoginPlain")}</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeSlideUp { from { opacity:0;transform:translateY(20px); } to { opacity:1;transform:translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .rp-input {
          width:100%;padding:12px 16px;background:rgba(255,255,255,.70);
          border:1px solid rgba(255,255,255,.85);border-radius:12px;color:${TEXT_DARK};
          font-size:.92rem;outline:none;transition:border-color .2s,box-shadow .2s;
          backdrop-filter:blur(8px);box-sizing:border-box;
        }
        .rp-input:focus { border-color:rgba(8,145,178,.7);box-shadow:0 0 0 3px rgba(8,145,178,.15); }
        .rp-btn {
          width:100%;padding:13px;border:none;border-radius:12px;font-size:.97rem;
          font-weight:700;cursor:pointer;transition:transform .15s,box-shadow .2s;
          background:linear-gradient(135deg,#06b6d4,${TEAL},${TEAL_DARK});color:#fff;
          box-shadow:0 4px 20px rgba(8,145,178,.40);
        }
        .rp-btn:hover:not(:disabled) { transform:translateY(-1px); }
        .rp-btn:disabled { opacity:.6;cursor:not-allowed; }
        .rp-toggle {
          position:absolute;right:13px;top:50%;transform:translateY(-50%);
          background:none;border:none;cursor:pointer;color:rgba(12,74,94,.42);padding:4px;
        }
        .accent-bar {
          width:48px;height:3px;background:${TEAL};border-radius:2px;
          margin:0 auto 14px;
        }
      `}</style>

      <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:BG_GRADIENT,position:"relative",overflow:"hidden" }}>
        <div style={{ width:"100%",maxWidth:420,margin:"1.5rem",animation:"fadeSlideUp .5s cubic-bezier(.22,1,.36,1) both" }}>
          <div style={{ background:"rgba(255,255,255,.75)",backdropFilter:"blur(28px) saturate(180%)",borderRadius:24,border:"1px solid rgba(255,255,255,.85)",boxShadow:"0 12px 36px rgba(8,145,178,.10)",padding:"2.5rem 2.25rem" }}>

            <div style={{ display:"flex",justifyContent:"center",marginBottom:"1.25rem" }}>
              <img src="/helixscan-logo.png" alt="HelixScan" style={{ height:42,objectFit:"contain" }} />
            </div>

            {done ? (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:"2.5rem",marginBottom:"1rem" }}>✅</div>
                <h2 style={{ margin:"0 0 12px",fontSize:"1.1rem",fontWeight:700,color:TEXT_DARK }}>{t("auth.pwdUpdatedTitle")}</h2>
                <p style={{ margin:"0 0 1.5rem",fontSize:"0.88rem",color:TEXT_MUTED,lineHeight:1.6 }}>
                  {t("auth.pwdUpdatedText1")}<br />{t("auth.pwdUpdatedText2")}
                </p>
                <Link to="/login" style={{ color:TEAL,fontWeight:600,fontSize:"0.88rem",textDecoration:"none" }}>{t("auth.goToLoginNow")}</Link>
              </div>
            ) : (
              <>
                {name && (
                  <p style={{ margin:"0 0 6px",fontSize:"0.95rem",fontWeight:600,color:TEAL,textAlign:"center" }}>
                    {t("auth.greeting", { name })}
                  </p>
                )}
                <div className="accent-bar" />
                <h2 style={{ margin:"0 0 8px",fontSize:"1.1rem",fontWeight:700,color:TEXT_DARK,textAlign:"center" }}>{t("auth.resetTitle")}</h2>
                <p style={{ margin:"0 0 1.75rem",fontSize:"0.83rem",color:TEXT_MUTED,textAlign:"center" }}>
                  {t("auth.resetDesc")}
                </p>

                {error && (
                  <div style={{ background:"rgba(220,38,38,.08)",border:"1px solid rgba(220,38,38,.25)",borderRadius:10,padding:"10px 14px",color:"#dc2626",fontSize:"0.84rem",marginBottom:"1rem" }}>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom:"1rem" }}>
                    <label style={{ display:"block",fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.7px",textTransform:"uppercase",color:TEXT_MUTED,marginBottom:6 }}>{t("auth.newPassword")}</label>
                    <div style={{ position:"relative" }}>
                      <input
                        className="rp-input"
                        type={showPass ? "text" : "password"}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder={t("auth.newPasswordPlaceholder")}
                        required
                        autoFocus
                        style={{ paddingRight:44 }}
                      />
                      <button type="button" className="rp-toggle" onClick={() => setShowPass(v => !v)} tabIndex={-1}>
                        {showPass ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                            <line x1="1" y1="1" x2="23" y2="23"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom:"1.75rem" }}>
                    <label style={{ display:"block",fontSize:"0.72rem",fontWeight:700,letterSpacing:"0.7px",textTransform:"uppercase",color:TEXT_MUTED,marginBottom:6 }}>{t("auth.confirmPassword")}</label>
                    <input
                      className="rp-input"
                      type={showPass ? "text" : "password"}
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      placeholder={t("auth.confirmPasswordPlaceholder")}
                      required
                    />
                    {confirm && password !== confirm && (
                      <p style={{ margin:"6px 0 0",fontSize:"0.78rem",color:"#dc2626" }}>{t("auth.pwdMismatch")}</p>
                    )}
                  </div>

                  <button type="submit" className="rp-btn" disabled={loading || !password || !confirm}>
                    {loading ? (
                      <span style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:10 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation:"spin .8s linear infinite" }}>
                          <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                        </svg>
                        {t("auth.saving")}
                      </span>
                    ) : t("auth.saveNewPassword")}
                  </button>
                </form>
              </>
            )}
          </div>
          <p style={{ margin:"1.25rem 0 0",fontSize:"0.75rem",color:TEXT_MUTED,textAlign:"center",letterSpacing:"0.3px" }}>
            {t("auth.copyright")}
          </p>
        </div>
      </div>
    </>
  );
}
